import React, { memo } from "react";
import { Box, Button, Checkbox, TableCell, TableRow } from "@mui/material";
import { stageLabel } from "../utils/stages";

// Eine Zeile des Werkskatalogs (ein Werk). Memoisiert je Werk: Bei einer Änderung rendert nur die Zeile neu, deren
// eigener Eintrag (`own`) sich ändert. Keine Tooltips je Zelle; „automatisch“ und „Basisspalte“ stehen im aria-label,
// die Erklärung in der Legende (aria-describedby).
const DEFAULT_ID = "default";
const idOf = row => row.isDefault ? DEFAULT_ID : row.targetId;
const colName = row => row.instanceName || row.environment || row.targetId;
const plantLabel = plant => [plant.name, plant.kennung].filter(Boolean).join(" ");
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const noteSx = { display: "block", fontSize: 12, color: "var(--iio-text-3)", fontFamily: "inherit" };
const nameSx = { fontWeight: 600, overflowWrap: "anywhere" };
const kenSx = { fontSize: 12, color: "var(--iio-text-2)", ml: 1 };
export const resetButtonSx = { textTransform: "none", fontFamily: "inherit", color: "var(--iio-text-2)", borderRadius: "6px", minHeight: 32, whiteSpace: "nowrap",
  "&:hover": { color: "var(--iio-text-1)", background: "var(--iio-bg-3)" }, "&.Mui-focusVisible": { outline: "2px solid var(--iio-brand-fg)", outlineOffset: 2 } };

/** Props: plant, columns, baseStage, own (entries[kennung] oder undefined), locked, disabled, readOnly, onToggle, onReset, t. */
export function CatalogRowView({ plant, columns, baseStage, own, locked, disabled, readOnly, onToggle, onReset, t }) {
  const custom = !!own;
  return (
    <TableRow>
      <TableCell component="th" scope="row" className="ptc-plant">
        <Box component="span" sx={nameSx}>{plant.name || plant.kennung}</Box>
        {plant.name && <Box component="span" sx={kenSx}>{plant.kennung}</Box>}
        {!custom && <Box component="span" sx={noteSx}>{t("plantTargets.defaults")}</Box>}
      </TableCell>
      {columns.map(stage => stage.rows.map((row, i) => {
        const id = idOf(row);
        const ownStage = own?.[stage.key] || null;
        const on = ownStage ? ownStage.includes(id) : row.isDefault;
        const base = on && stage.key === baseStage && row.isDefault;
        const auto = !ownStage && row.isDefault && !base;
        const label = t("plantTargets.cellLabel", { werk: plantLabel(plant), stage: stageLabel(stage.key), ziel: colName(row) })
          + (base ? `, ${t("plantTargets.cellBase")}` : auto ? `, ${t("plantTargets.cellAuto")}` : "");
        return <TableCell key={stage.key + id} align="center" className={[i === 0 && "ptc-groupstart", auto && "ptc-auto"].filter(Boolean).join(" ") || undefined}>
          <Checkbox size="small" disableRipple checked={on} disabled={base || auto || locked}
            inputProps={{ "aria-label": label, "aria-describedby": base || auto ? "cs-ptc-legend" : undefined, "data-auto": auto ? "true" : undefined }}
            onChange={() => onToggle(plant.kennung, stage.key, id)}/>
        </TableCell>;
      }))}
      {!readOnly && <TableCell>{custom && <Button variant="text" size="small" disableRipple disabled={disabled} sx={resetButtonSx}
        aria-label={t("plantTargets.resetFor", { werk: plantLabel(plant) })} onClick={() => onReset(plant.kennung)}>{t("plantTargets.resetShort")}</Button>}</TableCell>}
    </TableRow>
  );
}

const rowPropsEqual = (a, b) => a.plant === b.plant && a.columns === b.columns && a.baseStage === b.baseStage && a.locked === b.locked
  && a.disabled === b.disabled && a.readOnly === b.readOnly && a.onToggle === b.onToggle && a.onReset === b.onReset && a.t === b.t && same(a.own, b.own);
export const CatalogRow = memo(CatalogRowView, rowPropsEqual);
